import React from 'react';
import { Calculator, Leaf, Sparkles, Wine } from 'lucide-react';

const Features = ({ language }) => {
  const texts = {
    en: {
      title: 'Introduction',
      subtitle: 'At Souschaif, we are revolutionizing how you cook and shop with smart, personalized recipe ideas.',
      features: [
        {
          icon: Leaf,
          title: 'Reduce Food Waste',
          description: 'Use what is already in your fridge or pantry and turn everyday ingredients into delicious meals.'
        },
        {
          icon: Sparkles,
          title: 'Personalized AI Recipes',
          description: 'Get tailored meal ideas based on your ingredients, preferences, allergies, and nutritional needs.'
        },
        {
          icon: Wine,
          title: 'Perfect Pairings',
          description: 'Elevate your meals with wine pairing inspiration for cozy dinners or nights with friends.'
        },
        {
          icon: Calculator,
          title: 'Save Money',
          description: 'Plan smarter shopping lists and track the environmental and financial impact of wasted food.'
        }
      ]
    },
    sl: {
      title: 'Uvod',
      subtitle: 'Souschaif spreminja način kuhanja in nakupovanja s pametnimi, personaliziranimi idejami za recepte.',
      features: [
        {
          icon: Leaf,
          title: 'Manj odpadne hrane',
          description: 'Uporabite sestavine, ki jih že imate v hladilniku ali shrambi, in jih spremenite v okusne obroke.'
        },
        {
          icon: Sparkles,
          title: 'Personalizirani AI recepti',
          description: 'Prejmite ideje za obroke glede na sestavine, okus, alergije in prehranske potrebe.'
        },
        {
          icon: Wine,
          title: 'Popolne kombinacije',
          description: 'Dopolnite jedi z idejami za vinske kombinacije za domače večerje ali druženja s prijatelji.'
        },
        {
          icon: Calculator,
          title: 'Prihranite denar',
          description: 'Načrtujte pametnejše nakupovalne sezname in spremljajte vpliv manjše količine zavržene hrane.'
        }
      ]
    }
  };

  const t = texts[language];

  return (
    <section id="section-2" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.features.map((feature, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-emerald-50 to-lime-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {feature.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Features Preview Image */}
        <div className="mt-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-lime-600/20 rounded-3xl blur-3xl transform scale-110"></div>
          <div className="relative bg-gradient-to-br from-emerald-50 to-lime-50 rounded-3xl p-8 shadow-2xl">
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?auto=format&fit=crop&w=1400&q=80"
                alt={language === 'en' ? 'Home cook preparing fresh vegetables in the kitchen' : 'Domači kuhar pripravlja svežo zelenjavo v kuhinji'}
                className="w-full h-[420px] object-cover rounded-2xl transform hover:scale-105 transition-transform duration-700"
              />
              
              {/* Overlay badges */}
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-bold text-gray-800">
                    {language === 'en' ? 'Smart Meal Ideas' : 'Pametne ideje za obroke'}
                  </span>
                </div>
              </div>
              
              <div className="absolute bottom-6 right-6 bg-emerald-600/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                <span className="text-sm font-semibold text-white">
                  {language === 'en' ? 'Less Waste' : 'Manj odpadkov'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
